/**
 * UI Label Configuration — Plug-N-Play-Infra.ai
 *
 * Centralized text strings for all user-facing labels.
 * Change these ONCE before a demo to protect IP.
 * No need to hunt through component files.
 */

const UI_LABELS = {
  // AI Layer Names (sanitized for demos)
  RAG_AI: 'Boundary Guardrails',
  GENAI: 'AI',
  AGENTIC_AI: 'Comm-AI',

  // Full names
  RAG_AI_FULL: 'Boundary Guardrails Configuration',
  GENAI_FULL: 'AI Operations',
  AGENTIC_AI_FULL: 'Comm-AI Configuration',

  // Tab labels in client application
  GENAI_TAB: 'AI Ops',
  AGENTIC_TAB: 'Comm-AI',

  // Client Type descriptions
  STANDALONE_DESC: 'Independent business with its own AI and Comm-AI configuration',
  CHAIN_OWNER_DESC: 'Parent tenant that defines global policies inherited by all locations',
  SUB_CLIENT_DESC: 'Inherits Parent configuration from a parent chain but uses its own local database',

  // Application Model descriptions
  FRANCHISE_DESC: 'Multi-location business with shared branding, centralized policies, and location-level operations using AI',
  RETAIL_SUB_DESC: 'Single-location or independent business subscribing to the AI service',

  // Upload step labels
  UPLOAD_RAG: 'Guardrails',
  UPLOAD_RAG_DESC: 'Policies, handbooks, compliance rules, CEO directives',
  UPLOAD_GENAI: 'AI',
  UPLOAD_GENAI_DESC: 'Business operations, DB instructions, use cases',
  UPLOAD_AGENTIC: 'Comm-AI',
  UPLOAD_AGENTIC_DESC: 'Communication tokens, agreement templates, comm rules',

  // Status labels
  RAG_STATUS: 'Guardrails Status',
  GENAI_STATUS: 'AI Status',
  AGENTIC_STATUS: 'Comm-AI Status',

  // Icons
  RAG_ICON: '🛡️',
  GENAI_ICON: '⚙️',
  AGENTIC_ICON: '📡',
};

export default UI_LABELS;

// ─────────────────────────────────────────────────────────────────────────────
// Product-Type-Specific UI Labels
// Maps product_type to contextual labels for RAG-AI, GenAI, Agentic-AI tabs,
// Smart Decisions Dashboard, and menu/button content.
// ─────────────────────────────────────────────────────────────────────────────

export const PRODUCT_TYPE_LABELS = {
  'China-US-Strategic-Intel.Type-1': {
    RAG_AI: 'CEO Directives & Governance',
    GENAI: 'GeoPulse Intelligence',
    AGENTIC_AI: 'Executive Alerts',
    RAG_AI_FULL: 'CEO Intelligence Directives Configuration',
    GENAI_FULL: 'Geopolitical Decision Intelligence',
    AGENTIC_AI_FULL: 'Executive Alert & Notification Configuration',
    GENAI_TAB: 'AI Ops',
    AGENTIC_TAB: 'Executive Orchestration',
    UPLOAD_RAG_DESC: 'CEO directives, portfolio holdings, supply chain maps, governance policies',
    UPLOAD_GENAI_DESC: 'Geopolitical event database, sector models, corporate exposure data',
    UPLOAD_AGENTIC_DESC: 'Alert thresholds, Slack/webhook tokens, delivery preferences',
    DASHBOARD_TITLE: '🌏 GeoPulse Decisions Dashboard',
    ANALYTICS_TITLE: '📊 GeoPulse Analytics',
  },
  'Procurement Intelligence.Type-1 (Prototyped for Tamil Nadu Govt)': {
    RAG_AI: 'Policy & Compliance Rules',
    GENAI: 'Procurement Intelligence',
    AGENTIC_AI: 'Workflow Automation',
    RAG_AI_FULL: 'Procurement Policy & Compliance Configuration',
    GENAI_FULL: 'Procurement Analytics Intelligence',
    AGENTIC_AI_FULL: 'Procurement Workflow & Approval Automation',
    GENAI_TAB: 'AI Ops',
    AGENTIC_TAB: 'Workflows',
    UPLOAD_RAG_DESC: 'TN Tenders Act, GFR 2017, CM directives, department SOPs',
    UPLOAD_GENAI_DESC: 'Tender database, vendor registry, contract management, budget data',
    UPLOAD_AGENTIC_DESC: 'Approval workflow tokens, notification rules, escalation policies',
    DASHBOARD_TITLE: '🏛️ Procurement Decisions Dashboard',
    ANALYTICS_TITLE: '📊 Procurement Analytics',
  },
  'Financial-Bank-Type1': {
    RAG_AI: 'Regulatory Compliance',
    GENAI: 'Banking Intelligence',
    AGENTIC_AI: 'Risk Alerts',
    RAG_AI_FULL: 'Regulatory Compliance & Risk Governance',
    GENAI_FULL: 'Banking & Financial Risk Intelligence',
    AGENTIC_AI_FULL: 'Risk Alert & Regulatory Notification Configuration',
    GENAI_TAB: 'AI Ops',
    AGENTIC_TAB: 'Risk Alerts',
    UPLOAD_RAG_DESC: 'Basel III/IV rules, internal risk policies, audit requirements',
    UPLOAD_GENAI_DESC: 'Balance sheet, loan portfolio, ALM data, stress scenarios',
    UPLOAD_AGENTIC_DESC: 'Risk threshold alerts, regulatory filing tokens, escalation rules',
    DASHBOARD_TITLE: '🏦 Banking Decisions Dashboard',
    ANALYTICS_TITLE: '📊 Bank Analytics',
  },
  'Restaurant.Type-1': {
    RAG_AI: 'Food Safety & Policies',
    GENAI: 'Restaurant Intelligence',
    AGENTIC_AI: 'Vendor & Comm',
    RAG_AI_FULL: 'Food Safety & Operational Policy Configuration',
    GENAI_FULL: 'Restaurant Operations Intelligence',
    AGENTIC_AI_FULL: 'Vendor Communication & Agreement Configuration',
    GENAI_TAB: 'AI Ops',
    AGENTIC_TAB: 'Comm-AI',
    UPLOAD_RAG_DESC: 'Food safety policies, employee handbook, health compliance rules',
    UPLOAD_GENAI_DESC: 'Menu data, orders, inventory, reservations, staff schedules',
    UPLOAD_AGENTIC_DESC: 'Vendor communication tokens, agreement templates, delivery partner rules',
    DASHBOARD_TITLE: '🍽️ Restaurant Decisions Dashboard',
    ANALYTICS_TITLE: '📊 Restaurant Analytics',
  },
  'Gym.Type-1': {
    RAG_AI: 'Safety & Membership Rules',
    GENAI: 'Fitness Intelligence',
    AGENTIC_AI: 'Member Comm',
    RAG_AI_FULL: 'Safety & Membership Policy Configuration',
    GENAI_FULL: 'Fitness Operations Intelligence',
    AGENTIC_AI_FULL: 'Member Communication & Engagement Configuration',
    GENAI_TAB: 'AI Ops',
    AGENTIC_TAB: 'Comm-AI',
    UPLOAD_RAG_DESC: 'Safety protocols, membership terms, trainer certification rules',
    UPLOAD_GENAI_DESC: 'Memberships, classes, trainers, equipment, attendance data',
    UPLOAD_AGENTIC_DESC: 'Member notification tokens, renewal reminders, class booking rules',
    DASHBOARD_TITLE: '💪 Fitness Decisions Dashboard',
    ANALYTICS_TITLE: '📊 Fitness Analytics',
  },
  'Insurance.Type-1': {
    RAG_AI: 'Underwriting Rules',
    GENAI: 'Insurance Intelligence',
    AGENTIC_AI: 'Claims Comm',
    RAG_AI_FULL: 'Underwriting & Compliance Rule Configuration',
    GENAI_FULL: 'Insurance Operations Intelligence',
    AGENTIC_AI_FULL: 'Claims Communication & Agent Notification Configuration',
    GENAI_TAB: 'AI Ops',
    AGENTIC_TAB: 'Comm-AI',
    UPLOAD_RAG_DESC: 'Underwriting guidelines, claims policies, regulatory compliance rules',
    UPLOAD_GENAI_DESC: 'Policies, premiums, claims, agents, offices, loss ratios',
    UPLOAD_AGENTIC_DESC: 'Agent notification tokens, claims communication templates, renewal rules',
    DASHBOARD_TITLE: '🛡️ Insurance Decisions Dashboard',
    ANALYTICS_TITLE: '📊 Insurance Analytics',
  },
  'Grocery.Type-1': {
    RAG_AI: 'Food Safety & Store Policies',
    GENAI: 'Grocery Intelligence',
    AGENTIC_AI: 'Supplier Comm',
    RAG_AI_FULL: 'Food Safety & Store Policy Configuration',
    GENAI_FULL: 'Grocery & Restaurant Operations Intelligence',
    AGENTIC_AI_FULL: 'Supplier Communication & Order Configuration',
    GENAI_TAB: 'AI Ops',
    AGENTIC_TAB: 'Comm-AI',
    UPLOAD_RAG_DESC: 'Food safety rules, store policies, employee handbook, loyalty program terms',
    UPLOAD_GENAI_DESC: 'Products, inventory, POS transactions, kitchen orders, suppliers',
    UPLOAD_AGENTIC_DESC: 'Supplier communication tokens, order templates, delivery coordination rules',
    DASHBOARD_TITLE: '🛒 Grocery Decisions Dashboard',
    ANALYTICS_TITLE: '📊 Grocery Analytics',
  },
  'Logistics.Type-1': {
    RAG_AI: 'Safety & Compliance',
    GENAI: 'Fleet Intelligence',
    AGENTIC_AI: 'Dispatch Comm',
    RAG_AI_FULL: 'Fleet Safety & DOT Compliance Configuration',
    GENAI_FULL: 'Fleet & Logistics Operations Intelligence',
    AGENTIC_AI_FULL: 'Dispatch Communication & Contractor Configuration',
    GENAI_TAB: 'AI Ops',
    AGENTIC_TAB: 'Comm-AI',
    UPLOAD_RAG_DESC: 'DOT compliance, safety protocols, driver policies, insurance requirements',
    UPLOAD_GENAI_DESC: 'Routes, vehicles, drivers, deliveries, fuel, maintenance records',
    UPLOAD_AGENTIC_DESC: 'Dispatch tokens, contractor communication templates, SLA alert rules',
    DASHBOARD_TITLE: '🚛 Logistics Decisions Dashboard',
    ANALYTICS_TITLE: '📊 Logistics Analytics',
  },
  'RealEstate.Type-1': {
    RAG_AI: 'Compliance & Policies',
    GENAI: 'Realty Intelligence',
    AGENTIC_AI: 'Client Comm',
    RAG_AI_FULL: 'Real Estate Compliance & Policy Configuration',
    GENAI_FULL: 'Real Estate Operations Intelligence',
    AGENTIC_AI_FULL: 'Client Communication & Listing Configuration',
    GENAI_TAB: 'AI Ops',
    AGENTIC_TAB: 'Comm-AI',
    UPLOAD_RAG_DESC: 'Fair housing rules, brokerage policies, disclosure requirements',
    UPLOAD_GENAI_DESC: 'Listings, transactions, agents, leads, rentals, commissions',
    UPLOAD_AGENTIC_DESC: 'Client notification tokens, listing templates, showing coordination rules',
    DASHBOARD_TITLE: '🏠 Realty Decisions Dashboard',
    ANALYTICS_TITLE: '📊 Realty Analytics',
  },
  'Medical.Type-1': {
    RAG_AI: 'HIPAA & Clinical Policies',
    GENAI: 'Medical Intelligence',
    AGENTIC_AI: 'Patient Comm',
    RAG_AI_FULL: 'HIPAA & Clinical Policy Configuration',
    GENAI_FULL: 'Medical Practice Intelligence',
    AGENTIC_AI_FULL: 'Patient Communication & Appointment Configuration',
    GENAI_TAB: 'AI Ops',
    AGENTIC_TAB: 'Comm-AI',
    UPLOAD_RAG_DESC: 'HIPAA policies, clinical protocols, consent forms, insurance rules',
    UPLOAD_GENAI_DESC: 'Patients, appointments, treatments, billing, claims, supplies',
    UPLOAD_AGENTIC_DESC: 'Patient notification tokens, appointment reminders, referral templates',
    DASHBOARD_TITLE: '🏥 Medical Decisions Dashboard',
    ANALYTICS_TITLE: '📊 Medical Analytics',
  },
  'HVAC.Type-1': {
    RAG_AI: 'Safety & Licensing',
    GENAI: 'HVAC Intelligence',
    AGENTIC_AI: 'Customer Comm',
    RAG_AI_FULL: 'HVAC Safety & Licensing Configuration',
    GENAI_FULL: 'HVAC Service Operations Intelligence',
    AGENTIC_AI_FULL: 'Customer Communication & Scheduling Configuration',
    GENAI_TAB: 'AI Ops',
    AGENTIC_TAB: 'Comm-AI',
    UPLOAD_RAG_DESC: 'EPA regulations, safety protocols, licensing requirements, warranty policies',
    UPLOAD_GENAI_DESC: 'Service calls, technicians, parts inventory, contracts, warranties',
    UPLOAD_AGENTIC_DESC: 'Customer notification tokens, scheduling templates, maintenance reminders',
    DASHBOARD_TITLE: '❄️ HVAC Decisions Dashboard',
    ANALYTICS_TITLE: '📊 HVAC Analytics',
  },
  'Lawn.Type-1': {
    RAG_AI: 'Safety & Environmental',
    GENAI: 'Lawn Intelligence',
    AGENTIC_AI: 'Customer Comm',
    RAG_AI_FULL: 'Environmental & Safety Policy Configuration',
    GENAI_FULL: 'Lawn & Landscape Operations Intelligence',
    AGENTIC_AI_FULL: 'Customer Communication & Scheduling Configuration',
    GENAI_TAB: 'AI Ops',
    AGENTIC_TAB: 'Comm-AI',
    UPLOAD_RAG_DESC: 'Chemical application rules, environmental policies, safety protocols',
    UPLOAD_GENAI_DESC: 'Jobs, crews, equipment, customers, zones, seasonal data',
    UPLOAD_AGENTIC_DESC: 'Customer notification tokens, scheduling templates, seasonal reminders',
    DASHBOARD_TITLE: '🌿 Lawn & Landscape Decisions Dashboard',
    ANALYTICS_TITLE: '📊 Lawn & Landscape Analytics',
  },
  'IndustrialWater-PFAS-Intelligence.Type-2': {
    RAG_AI: 'PFAS Regulatory Boundary',
    GENAI: 'Water Intelligence',
    AGENTIC_AI: 'Operations Automation',
    RAG_AI_FULL: 'PFAS Regulatory & Compliance Policy Configuration',
    GENAI_FULL: 'Industrial Water Operations Intelligence',
    AGENTIC_AI_FULL: 'Operations Automation & Decision Configuration',
    GENAI_TAB: 'AI Ops',
    AGENTIC_TAB: 'Executive Orchestration',
    UPLOAD_RAG_DESC: 'EPA PFAS rules, NC DEQ policies, CEO directives, LOB constraints',
    UPLOAD_GENAI_DESC: 'Membrane performance, water quality, SCADA, KPIs, compliance data',
    UPLOAD_AGENTIC_DESC: 'Agent thresholds, approval workflow rules, escalation policies',
    DASHBOARD_TITLE: '💧 Industrial Water Decisions Dashboard',
    ANALYTICS_TITLE: '📊 Water & PFAS Analytics',
  },
};

/**
 * Get product-type-specific label, falling back to default UI_LABELS.
 * @param {string} productType - The client's product_type
 * @param {string} key - Label key (e.g. 'RAG_AI', 'GENAI', 'DASHBOARD_TITLE')
 * @returns {string}
 */
export function getProductTypeLabel(productType, key) {
  const ptLabels = PRODUCT_TYPE_LABELS[productType];
  if (ptLabels && ptLabels[key]) return ptLabels[key];
  return UI_LABELS[key] || key;
}

// ─────────────────────────────────────────────────────────────────────────────
// TN-IPDS — Tamil Nadu Procurement Intelligence Labels
// ProductType: Procurement Intelligence.Type-1 (Prototyped for Tamil Nadu Govt)
//
// Bilingual: English (en) + Tamil (ta)
// Tamil for officer roles (tn_procurement_officer, tn_accepting_authority, etc.)
// English for admin/audit roles
// ─────────────────────────────────────────────────────────────────────────────

export const TN_LABELS = {
  en: {
    // Navigation
    nav_tenders:            'Tenders',
    nav_vendors:            'Vendors',
    nav_policy_qa:          'Policy Q&A',
    nav_risk_cases:         'Risk Cases',
    nav_workflow_queue:     'Workflow Queue',
    nav_district_dashboard: 'District Dashboard',
    nav_cm_directives:      'CM Directives',
    nav_audit_trail:        'Audit Trail',

    // Dashboard titles
    title_district_dashboard: 'District Procurement Dashboard',
    title_risk_cases:         'AI Risk Case Review',
    title_policy_qa:          'Procurement Policy Q&A',
    title_tender_upload:      'Tender Upload & Intake',
    title_cm_directives:      'CM Directive KPI Targets',
    title_audit_trail:        'Procurement Audit Trail',

    // Metric labels
    metric_total_tenders:     'Total Tenders',
    metric_awarded:           'Awarded',
    metric_open_tenders:      'Open Tenders',
    metric_total_contracted:  'Total Contracted',
    metric_msme_vendors:      'MSME Vendors',
    metric_open_risk_cases:   'Open Risk Cases',
    metric_single_bid_rate:   'Single-Bid Rate',
    metric_avg_bid_window:    'Avg Bid Window',
    metric_budget_utilization:'Budget Utilization',
    metric_compliance_rate:   'Compliance Rate',

    // Risk indicator terminology (non-accusatory)
    risk_short_bid_window:          'Short Bid Window',
    risk_repeated_vendor_wins:      'Repeated Vendor Award Pattern',
    risk_identical_bid_pricing:     'Coordinated Bidding Pattern',
    risk_restricted_specifications: 'Restrictive Specification Concern',
    risk_post_award_variation:      'Post-Award Variation',
    risk_single_bid_accepted:       'Single-Bid Acceptance',
    risk_repeated_emergency:        'Repeated Emergency Procurement',
    risk_price_variance:            'Price Variance Outlier',
    risk_premature_payment:         'Premature Payment Concern',
    risk_overdue_appeals:           'Overdue Appeal',

    // Severity levels
    severity_critical: 'Critical',
    severity_high:     'High',
    severity_medium:   'Medium',
    severity_low:      'Low',

    // Actions
    action_confirm_issue:  'Confirm Issue',
    action_false_positive: 'False Positive',
    action_dismiss:        'Dismiss',
    action_approve:        'Approve',
    action_reject:         'Reject',

    // Currency / date
    currency_symbol: '₹',
    fiscal_year_label: 'Fiscal Year (Apr–Mar)',
    timezone_label: 'IST (Asia/Kolkata)',
  },

  ta: {
    // Navigation (Tamil)
    nav_tenders:            'டெண்டர்கள்',
    nav_vendors:            'விற்பனையாளர்கள்',
    nav_policy_qa:          'கொள்கை கேள்வி-பதில்',
    nav_risk_cases:         'ஆபத்து வழக்குகள்',
    nav_workflow_queue:     'பணிப்பாய்வு வரிசை',
    nav_district_dashboard: 'மாவட்ட டாஷ்போர்டு',
    nav_cm_directives:      'முதலமைச்சர் உத்தரவுகள்',
    nav_audit_trail:        'தணிக்கை பதிவு',

    // Dashboard titles (Tamil)
    title_district_dashboard: 'மாவட்ட கொள்முதல் டாஷ்போர்டு',
    title_risk_cases:         'AI ஆபத்து வழக்கு மதிப்பாய்வு',
    title_policy_qa:          'கொள்முதல் கொள்கை கேள்வி-பதில்',
    title_tender_upload:      'டெண்டர் பதிவேற்றம்',
    title_cm_directives:      'முதலமைச்சர் உத்தரவு இலக்குகள்',
    title_audit_trail:        'கொள்முதல் தணிக்கை பதிவு',

    // Metric labels (Tamil)
    metric_total_tenders:     'மொத்த டெண்டர்கள்',
    metric_awarded:           'வழங்கப்பட்டவை',
    metric_open_tenders:      'திறந்த டெண்டர்கள்',
    metric_total_contracted:  'மொத்த ஒப்பந்த மதிப்பு',
    metric_msme_vendors:      'MSME விற்பனையாளர்கள்',
    metric_open_risk_cases:   'திறந்த ஆபத்து வழக்குகள்',
    metric_single_bid_rate:   'ஒற்றை ஏலம் விகிதம்',
    metric_avg_bid_window:    'சராசரி ஏல காலம்',
    metric_budget_utilization:'பட்ஜெட் பயன்பாடு',
    metric_compliance_rate:   'இணக்க விகிதம்',

    // Risk indicator terminology (Tamil — non-accusatory)
    risk_short_bid_window:          'குறுகிய ஏல காலம்',
    risk_repeated_vendor_wins:      'திரும்பத்திரும்ப விற்பனையாளர் விருது முறை',
    risk_identical_bid_pricing:     'ஒருங்கிணைந்த ஏல முறை',
    risk_restricted_specifications: 'கட்டுப்படுத்தப்பட்ட விவரக்குறிப்பு கவலை',
    risk_post_award_variation:      'விருது பிந்தைய மாறுபாடு',
    risk_single_bid_accepted:       'ஒற்றை ஏல ஏற்பு',
    risk_repeated_emergency:        'திரும்பத்திரும்ப அவசர கொள்முதல்',
    risk_price_variance:            'விலை மாறுபாடு',
    risk_premature_payment:         'முன்கூட்டிய கட்டண கவலை',
    risk_overdue_appeals:           'தாமதமான மேல்முறையீடு',

    // Severity levels (Tamil)
    severity_critical: 'மிக முக்கியமான',
    severity_high:     'அதிக',
    severity_medium:   'நடுத்தர',
    severity_low:      'குறைந்த',

    // Actions (Tamil)
    action_confirm_issue:  'சிக்கலை உறுதிப்படுத்து',
    action_false_positive: 'தவறான எச்சரிக்கை',
    action_dismiss:        'நிராகரி',
    action_approve:        'அனுமதி',
    action_reject:         'நிராகரி',

    // Currency / date (Tamil)
    currency_symbol: '₹',
    fiscal_year_label: 'நிதியாண்டு (ஏப்-மார்)',
    timezone_label: 'IST (ஆசியா/கொல்கத்தா)',
  },
};

/**
 * Get a TN-IPDS label in the specified language.
 * Falls back to English if Tamil translation is missing.
 *
 * @param {string} key - Label key (e.g. 'nav_tenders')
 * @param {string} lang - 'en' or 'ta'
 * @returns {string}
 */
export function getTNLabel(key, lang = 'en') {
  const langMap = TN_LABELS[lang] || TN_LABELS.en;
  return langMap[key] || TN_LABELS.en[key] || key;
}

// ─────────────────────────────────────────────────────────────────────────────
// IW-PFAS — Industrial Water PFAS Intelligence Labels
// ProductType: IndustrialWater-PFAS-Intelligence.Type-2
// ─────────────────────────────────────────────────────────────────────────────

export const IW_LABELS = {
  // Units
  unit_ppt: 'ppt',
  unit_ppb: 'ppb',
  unit_ug_l: 'µg/L',
  unit_m3: 'm³',
  unit_kwh_m3: 'kWh/m³',
  unit_mgd: 'MGD',
  unit_bar: 'bar',
  unit_lmh: 'LMH',
  unit_lmh_bar: 'LMH/bar',
  unit_percent: '%',

  // Regulatory acronyms
  mcl: 'MCL',
  tmp: 'TMP',
  cip: 'CIP',
  sdwa: 'SDWA',
  cercla: 'CERCLA',
  tsca: 'TSCA',
  cwa: 'CWA',
  tri: 'TRI',
  npdwr: 'NPDWR',
  epa: 'EPA',
  nc_deq: 'NC DEQ',

  // PFAS analyte names
  analyte_pfoa: 'PFOA',
  analyte_pfos: 'PFOS',
  analyte_pfhxs: 'PFHxS',
  analyte_pfna: 'PFNA',
  analyte_pfbs: 'PFBS',
  analyte_hfpo_da: 'HFPO-DA (GenX)',
  analyte_pfas_total: 'Total PFAS',

  // Component titles
  title_overview: 'Industrial Water Overview',
  title_sites: 'Facility Sites',
  title_membrane_assets: 'Membrane Assets',
  title_water_quality: 'Water Quality',
  title_compliance_watchlist: 'Compliance Watchlist',
  title_risk_cases: 'Risk Cases',
  title_maintenance: 'Maintenance',
  title_inventory: 'Inventory & Suppliers',
  title_customers: 'Customer Exposure',
  title_policy_qa: 'Policy Q&A',
  title_executive_decisions: 'Executive Decisions',
  title_audit_trail: 'Audit Trail',

  // KPI labels
  kpi_pfas_compliance_risk: 'PFAS Compliance Risk',
  kpi_membrane_degradation: 'Membrane Degradation Risk',
  kpi_energy_intensity: 'Energy Intensity',
  kpi_mttr: 'Mean Time to Repair',
  kpi_pfas_rejection: 'PFAS Rejection Efficiency',
  kpi_stockout_risk: 'Inventory Stockout Risk',
  kpi_customer_exposure: 'Customer Regulatory Exposure',

  // Risk levels
  risk_red: 'Critical',
  risk_amber: 'Warning',
  risk_green: 'Normal',

  // Status labels
  status_compliant: 'Compliant',
  status_at_risk: 'At Risk',
  status_non_compliant: 'Non-Compliant',
  status_data_gap: 'Data Gap',
  status_pending: 'Pending',
  status_approved: 'Approved',
  status_rejected: 'Rejected',
  status_executed: 'Executed',

  // Refusal messages
  refusal_no_policy: 'No applicable policy found in the approved corpus for this query context.',
  refusal_legal: 'I cannot provide a legal conclusion; please consult Compliance/Legal.',
};

/**
 * Get an IW-PFAS label.
 * @param {string} key - Label key (e.g. 'unit_ppt', 'title_overview')
 * @returns {string}
 */
export function getIWLabel(key) {
  return IW_LABELS[key] || key;
}

/**
 * Format a PFAS concentration value with appropriate unit label.
 * @param {number} value
 * @param {string} unit - 'ppt', 'ppb', or 'ug/L'
 * @returns {string}
 */
export function formatPFASValue(value, unit = 'ppt') {
  if (value == null || isNaN(value)) return '—';
  const unitLabel = IW_LABELS[`unit_${unit}`] || unit;
  return `${Number(value).toFixed(2)} ${unitLabel}`;
}

/**
 * Format energy intensity value.
 * @param {number} value - kWh/m³
 * @returns {string}
 */
export function formatEnergyIntensity(value) {
  if (value == null || isNaN(value)) return '—';
  return `${Number(value).toFixed(2)} ${IW_LABELS.unit_kwh_m3}`;
}

/**
 * Determine the default language for a TN-IPDS user role.
 * Officer roles default to Tamil; admin/audit roles default to English.
 *
 * @param {string} role - RBAC role string
 * @returns {'en'|'ta'}
 */
export function getTNDefaultLanguage(role) {
  const officerRoles = [
    'tn_procurement_officer',
    'tn_accepting_authority',
    'tn_finance_officer',
  ];
  return officerRoles.includes(role) ? 'ta' : 'en';
}

/**
 * Format a number as INR with lakh/crore notation.
 * @param {number} value
 * @returns {string}
 */
export function formatINR(value) {
  if (value == null || isNaN(value)) return '—';
  const n = Number(value);
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(2)} L`;
  return `₹${n.toLocaleString('en-IN')}`;
}

/**
 * Format a date/timestamp in IST (Asia/Kolkata) timezone.
 * @param {string|Date} value
 * @returns {string}
 */
export function formatIST(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return String(value);
  }
}

/**
 * Get the Indian fiscal year string for a given date.
 * e.g. 2026-01-15 → "2025-26"
 * @param {Date} [date]
 * @returns {string}
 */
export function getIndianFiscalYear(date) {
  const d = date || new Date();
  const month = d.getMonth() + 1; // 1-12
  const year = d.getFullYear();
  if (month >= 4) {
    return `${year}-${String(year + 1).slice(2)}`;
  }
  return `${year - 1}-${String(year).slice(2)}`;
}
